import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth, PAGE_CATALOG } from "@/App";
import { FraisZonesEditor } from "@/components/FraisZonesEditor";
import MessageTemplatesEditor from "@/components/MessageTemplatesEditor";
import { GoogleSheetsSync } from "@/components/GoogleSheetsSync";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Settings, Save, RefreshCw, Euro, Percent, Image, Building2, Users, Trash2, Plus, Shield, Truck, FileText } from "lucide-react";

// ---------- Team Management (admin-only) ----------
const TeamManager = () => {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "livreur", permissions: ["livreur"] });
  const [creating, setCreating] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/auth/users`, { headers });
      setUsers(res.data);
    } catch {
      toast.error("Erreur chargement utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Preset helpers
  const applyPreset = (preset) => {
    if (preset === "admin") {
      setForm((f) => ({ ...f, role: "admin", permissions: ["*"] }));
    } else if (preset === "livreur") {
      setForm((f) => ({ ...f, role: "livreur", permissions: ["livreur"] }));
    } else {
      setForm((f) => ({ ...f, role: "custom", permissions: f.permissions.includes("*") ? [] : f.permissions }));
    }
  };

  const togglePermission = (key) => {
    setForm((f) => {
      // If user currently has "*", toggling individual perms → switch to custom mode with only that key
      if (f.permissions.includes("*")) {
        return { ...f, role: "custom", permissions: [key] };
      }
      const set = new Set(f.permissions);
      if (set.has(key)) set.delete(key); else set.add(key);
      const next = Array.from(set);
      // Auto-detect role: if permissions == ["livreur"] → role livreur, else custom
      let nextRole = f.role;
      if (next.length === 1 && next[0] === "livreur") nextRole = "livreur";
      else if (nextRole === "livreur") nextRole = "custom";
      return { ...f, role: nextRole, permissions: next };
    });
  };

  const openCreate = () => {
    setEditingUser(null);
    setForm({ name: "", email: "", password: "", role: "livreur", permissions: ["livreur"] });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role || "admin", permissions: u.permissions || [] });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.email || !form.name) {
      toast.error("Nom et email sont requis");
      return;
    }
    if (!editingUser && !form.password) {
      toast.error("Mot de passe requis");
      return;
    }
    if (form.permissions.length === 0) {
      toast.error("Cochez au moins une section accessible");
      return;
    }
    setCreating(true);
    try {
      if (editingUser) {
        const payload = { name: form.name, role: form.role, permissions: form.permissions };
        if (form.password) payload.password = form.password;
        await axios.put(`${API}/auth/users/${editingUser.id}`, payload, { headers });
        toast.success("Utilisateur mis à jour");
      } else {
        await axios.post(`${API}/auth/users`, form, { headers });
        toast.success("Utilisateur créé !");
      }
      setDialogOpen(false);
      setEditingUser(null);
      setForm({ name: "", email: "", password: "", role: "livreur", permissions: ["livreur"] });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Supprimer ${u.email} ?`)) return;
    try {
      await axios.delete(`${API}/auth/users/${u.id}`, { headers });
      toast.success("Utilisateur supprimé");
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur suppression");
    }
  };

  const roleBadge = (u) => {
    const r = u.role || "admin";
    if (r === "admin") return { label: "Admin", cls: "bg-indigo-50 text-indigo-700", icon: Shield };
    if (r === "livreur") return { label: "Livreur", cls: "bg-emerald-50 text-emerald-700", icon: Truck };
    return { label: "Personnalisé", cls: "bg-amber-50 text-amber-700", icon: Settings };
  };

  return (
    <Card data-testid="team-manager-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            Équipe & comptes utilisateurs
          </CardTitle>
          <CardDescription>Créez des comptes avec des permissions personnalisées par page.</CardDescription>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-4 text-center text-slate-400">Chargement…</div>
        ) : users.length === 0 ? (
          <div className="py-4 text-center text-slate-400">Aucun utilisateur</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {users.map((u) => {
              const isSelf = u.id === user?.id;
              const badge = roleBadge(u);
              const BadgeIcon = badge.icon;
              const permCount = (u.permissions || []).includes("*") ? "Toutes" : (u.permissions || []).length;
              return (
                <div key={u.id} className="flex items-center justify-between py-3 gap-3" data-testid={`user-row-${u.email}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${badge.cls}`}>
                      <BadgeIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {u.name}
                        {isSelf && <span className="ml-2 text-xs text-indigo-500">(vous)</span>}
                      </p>
                      <p className="text-xs text-slate-400 truncate">{u.email}</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>
                      {badge.label}
                    </span>
                    <span className="text-xs text-slate-400 hidden md:inline">
                      {permCount === "Toutes" ? "Accès complet" : `${permCount} section${permCount > 1 ? "s" : ""}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(u)} className="text-xs" data-testid={`edit-user-${u.email}`}>
                      Modifier
                    </Button>
                    {!isSelf && (
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(u)} className="text-red-500 hover:bg-red-50 h-8 w-8" data-testid={`delete-user-${u.email}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="add-user-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? "Modifier l'utilisateur" : "Ajouter un utilisateur"}</DialogTitle>
            <DialogDescription>Choisissez un rôle rapide ou personnalisez les accès.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Nom</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom de l'utilisateur" data-testid="new-user-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="utilisateur@topcocoon.com" data-testid="new-user-email" disabled={!!editingUser} />
              </div>
            </div>
            <div>
              <Label>{editingUser ? "Nouveau mot de passe (optionnel)" : "Mot de passe"}</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editingUser ? "Laisser vide pour ne pas changer" : "Mot de passe initial"} data-testid="new-user-password" />
            </div>

            {/* Rôle raccourci */}
            <div>
              <Label>Rôle rapide</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                <Button type="button" variant={form.role === "admin" ? "default" : "outline"} onClick={() => applyPreset("admin")} className={form.role === "admin" ? "bg-indigo-600 hover:bg-indigo-700" : ""} data-testid="preset-admin">
                  <Shield className="w-4 h-4 mr-1.5" />
                  Admin
                </Button>
                <Button type="button" variant={form.role === "livreur" ? "default" : "outline"} onClick={() => applyPreset("livreur")} className={form.role === "livreur" ? "bg-emerald-600 hover:bg-emerald-700" : ""} data-testid="preset-livreur">
                  <Truck className="w-4 h-4 mr-1.5" />
                  Livreur
                </Button>
                <Button type="button" variant={form.role === "custom" ? "default" : "outline"} onClick={() => applyPreset("custom")} className={form.role === "custom" ? "bg-amber-600 hover:bg-amber-700" : ""} data-testid="preset-custom">
                  <Settings className="w-4 h-4 mr-1.5" />
                  Personnalisé
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                {form.role === "admin" && "Accès complet à toutes les sections."}
                {form.role === "livreur" && "Accès uniquement au Mode Livreur (redirection automatique à la connexion)."}
                {form.role === "custom" && "Cochez exactement les sections auxquelles il/elle doit avoir accès."}
              </p>
            </div>

            {/* Permissions individuelles */}
            {form.role !== "admin" && (
              <div>
                <Label>Sections accessibles</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 p-3 bg-slate-50 rounded-lg border">
                  {PAGE_CATALOG.map((p) => (
                    <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white rounded px-2 py-1 transition-colors">
                      <Checkbox
                        checked={form.permissions.includes(p.key)}
                        onCheckedChange={() => togglePermission(p.key)}
                        data-testid={`perm-${p.key}`}
                      />
                      <span className="text-slate-700">{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-user-btn">
              {creating ? "Enregistrement…" : editingUser ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const Parametres = () => {
  const { token } = useAuth();
  const [params, setParams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchParams = async () => {
    try {
      const response = await axios.get(`${API}/parametres`, { headers });
      setParams(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/parametres`, params, { headers });
      toast.success("Paramètres sauvegardés !");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // Seuls ces champs sont numériques — tout le reste (email, téléphone, adresse,
  // TVA, IBAN, BIC, etc.) est traité comme texte pour éviter parseFloat sur des strings.
  const NUMERIC_FIELDS = new Set([
    "tva_rate", "commission", "start_cash", "budget_ads_mensuel", "depot_mensuel",
    "milan_cost", "louis_cost",
    "milan_price_b2c_ttc", "milan_price_b2b_ht",
    "louis_price_b2c_ttc", "louis_price_b2b_ht",
    "deliv_price_b2c_ttc", "deliv_price_b2b_ht",
    "frais_livraison_defaut",
  ]);

  const handleChange = (field, value) => {
    if (field === "sms_templates" || field === "email_templates") {
      setParams({ ...params, [field]: value });
    } else if (NUMERIC_FIELDS.has(field)) {
      setParams({ ...params, [field]: parseFloat(value) || 0 });
    } else {
      // Champs texte : company_name, logo_url, login_bg_url, vendeur_*, facture_*, b2b_contact_*
      setParams({ ...params, [field]: value });
    }
  };

  useEffect(() => {
    fetchParams();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="parametres-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Paramètres</h1>
          <p className="text-slate-500 mt-1">Configuration globale de TopCocoon</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="save-params-btn"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Sauvegarder
        </Button>
      </div>

      <FraisZonesEditor />

      <MessageTemplatesEditor />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Branding / Personnalisation */}
        <Card className="lg:col-span-2" data-testid="branding-params-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Image className="w-5 h-5 text-indigo-600" />
              Personnalisation
            </CardTitle>
            <CardDescription>Logo, image de fond et nom de l'entreprise</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Nom de l'entreprise</Label>
                <Input
                  type="text"
                  value={params?.company_name || "TopCocoon"}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                  placeholder="TopCocoon"
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>URL du Logo (carré, recommandé 200x200px)</Label>
                <Input
                  type="url"
                  value={params?.logo_url || ""}
                  onChange={(e) => handleChange("logo_url", e.target.value)}
                  placeholder="https://exemple.com/mon-logo.png"
                  data-testid="logo-url-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>URL de l'image de fond (page de connexion)</Label>
              <Input
                type="url"
                value={params?.login_bg_url || ""}
                onChange={(e) => handleChange("login_bg_url", e.target.value)}
                placeholder="https://exemple.com/background.jpg"
                data-testid="login-bg-url-input"
              />
            </div>
            
            {/* Preview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <div className="space-y-2">
                <Label className="text-slate-500">Aperçu du logo</Label>
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  {params?.logo_url ? (
                    <img 
                      src={params.logo_url} 
                      alt="Logo preview"
                      className="w-12 h-12 rounded-xl object-cover border"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : (
                    <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-xl">
                        {(params?.company_name || "T").charAt(0)}
                      </span>
                    </div>
                  )}
                  <span className="font-semibold text-slate-800">{params?.company_name || "TopCocoon"}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-500">Aperçu fond de connexion</Label>
                <div 
                  className="h-24 rounded-lg bg-cover bg-center border"
                  style={{ 
                    backgroundImage: `url('${params?.login_bg_url || "https://images.unsplash.com/photo-1729742768363-afa1a7074d89"}')` 
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* TVA & General */}
        <Card data-testid="general-params-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Percent className="w-5 h-5 text-indigo-600" />
              Paramètres Généraux
            </CardTitle>
            <CardDescription>TVA, commission et trésorerie</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Taux TVA (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={params?.tva_rate || 0}
                  onChange={(e) => handleChange("tva_rate", e.target.value)}
                  data-testid="tva-rate-input"
                />
                <p className="text-xs text-slate-500">Ex: 0.21 pour 21%</p>
              </div>
              <div className="space-y-2">
                <Label>Commission (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={params?.commission || 0}
                  onChange={(e) => handleChange("commission", e.target.value)}
                  data-testid="commission-input"
                />
                <p className="text-xs text-slate-500">Ex: 0.05 pour 5%</p>
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Trésorerie initiale (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={params?.start_cash || 0}
                  onChange={(e) => handleChange("start_cash", e.target.value)}
                  data-testid="start-cash-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Budget Ads mensuel (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={params?.budget_ads_mensuel || 0}
                  onChange={(e) => handleChange("budget_ads_mensuel", e.target.value)}
                  data-testid="budget-ads-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Dépôt mensuel (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={params?.depot_mensuel || 0}
                onChange={(e) => handleChange("depot_mensuel", e.target.value)}
                data-testid="depot-mensuel-input"
              />
            </div>
            <Separator />
            <div className="space-y-2">
              <Label>Lien public de précommande</Label>
              <Input
                value={params?.lien_precommande ?? ""}
                onChange={(e) => handleChange("lien_precommande", e.target.value)}
                placeholder="/precommande ou https://www.top-cocoon.be/precommande"
                data-testid="lien-precommande-input"
              />
              <p className="text-xs text-slate-500">URL complète OU route interne (préfixée par le domaine actuel)</p>
            </div>
            <div className="space-y-2">
              <Label>Lien public de prise de rendez-vous</Label>
              <Input
                value={params?.lien_rdv ?? ""}
                onChange={(e) => handleChange("lien_rdv", e.target.value)}
                placeholder="/prendre-rdv ou https://www.top-cocoon.be/prendre-rdv"
                data-testid="lien-rdv-input"
              />
              <p className="text-xs text-slate-500">Utilisé dans les SMS, blocs de partage et boutons Copier/Ouvrir</p>
            </div>
          </CardContent>
        </Card>

        {/* Milan Prices */}
        <Card data-testid="milan-params-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Euro className="w-5 h-5 text-indigo-600" />
              Prix Milan
            </CardTitle>
            <CardDescription>Coûts et prix de vente</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Coût Milan (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={params?.milan_cost || 0}
                onChange={(e) => handleChange("milan_cost", e.target.value)}
                data-testid="milan-cost-input"
              />
              <p className="text-xs text-slate-500">Coût rendu dépôt</p>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prix B2C TTC (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={params?.milan_price_b2c_ttc || 0}
                  onChange={(e) => handleChange("milan_price_b2c_ttc", e.target.value)}
                  data-testid="milan-price-b2c-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Prix B2B HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={params?.milan_price_b2b_ht || 0}
                  onChange={(e) => handleChange("milan_price_b2b_ht", e.target.value)}
                  data-testid="milan-price-b2b-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Louis Prices */}
        <Card data-testid="louis-params-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Euro className="w-5 h-5 text-purple-600" />
              Prix Louis King
            </CardTitle>
            <CardDescription>Coûts et prix de vente</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Coût Louis (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={params?.louis_cost || 0}
                onChange={(e) => handleChange("louis_cost", e.target.value)}
                data-testid="louis-cost-input"
              />
              <p className="text-xs text-slate-500">Coût rendu dépôt</p>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prix B2C TTC (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={params?.louis_price_b2c_ttc || 0}
                  onChange={(e) => handleChange("louis_price_b2c_ttc", e.target.value)}
                  data-testid="louis-price-b2c-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Prix B2B HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={params?.louis_price_b2b_ht || 0}
                  onChange={(e) => handleChange("louis_price_b2b_ht", e.target.value)}
                  data-testid="louis-price-b2b-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Prices */}
        <Card data-testid="delivery-params-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="w-5 h-5 text-emerald-600" />
              Livraison
            </CardTitle>
            <CardDescription>Prix de livraison par commande</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Livraison B2C TTC (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={params?.deliv_price_b2c_ttc || 0}
                  onChange={(e) => handleChange("deliv_price_b2c_ttc", e.target.value)}
                  data-testid="deliv-b2c-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Livraison B2B HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={params?.deliv_price_b2b_ht || 0}
                  onChange={(e) => handleChange("deliv_price_b2b_ht", e.target.value)}
                  data-testid="deliv-b2b-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary */}
      <Card className="bg-indigo-50 border-indigo-200" data-testid="params-summary">
        <CardContent className="p-6">
          <h3 className="font-semibold text-indigo-800 mb-4">Récapitulatif</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-indigo-600">Milan - Marge B2C</p>
              <p className="font-semibold text-indigo-900">
                {((params?.milan_price_b2c_ttc / (1 + params?.tva_rate)) - params?.milan_cost).toFixed(2)} €
              </p>
            </div>
            <div>
              <p className="text-indigo-600">Milan - Marge B2B</p>
              <p className="font-semibold text-indigo-900">
                {(params?.milan_price_b2b_ht - params?.milan_cost).toFixed(2)} €
              </p>
            </div>
            <div>
              <p className="text-indigo-600">Louis - Marge B2C</p>
              <p className="font-semibold text-indigo-900">
                {((params?.louis_price_b2c_ttc / (1 + params?.tva_rate)) - params?.louis_cost).toFixed(2)} €
              </p>
            </div>
            <div>
              <p className="text-indigo-600">Louis - Marge B2B</p>
              <p className="font-semibold text-indigo-900">
                {(params?.louis_price_b2b_ht - params?.louis_cost).toFixed(2)} €
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates SMS / Email */}
      <Card data-testid="templates-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-600" />
            Templates SMS & Email
          </CardTitle>
          <CardDescription>Variables disponibles : {"{prenom}"}, {"{lien}"}, {"{code}"}. Éditez les textes envoyés automatiquement à vos clients.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[
            { key: "fin_tournee", label: "SMS — Fin de tournée (envoi immédiat)" },
            { key: "j1", label: "SMS — J+1 (remerciement & avis)" },
            { key: "j7", label: "SMS — J+7 (parrainage)" },
            { key: "j30", label: "SMS — J+30 (relance)" },
          ].map((t) => (
            <div key={t.key}>
              <Label className="text-sm font-medium text-slate-700">{t.label}</Label>
              <textarea
                className="mt-1 w-full min-h-[70px] rounded-md border border-slate-200 p-2 text-sm"
                value={(params?.sms_templates || {})[t.key] || ""}
                onChange={(e) => handleChange("sms_templates", { ...(params.sms_templates || {}), [t.key]: e.target.value })}
                data-testid={`tpl-sms-${t.key}`}
              />
            </div>
          ))}

          <div className="pt-2 border-t">
            <Label className="text-sm font-medium text-slate-700">Email — Fin de tournée : Sujet</Label>
            <input
              type="text"
              className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm"
              value={(params?.email_templates?.fin_tournee || {}).subject || ""}
              onChange={(e) => handleChange("email_templates", { ...(params.email_templates || {}), fin_tournee: { ...((params.email_templates || {}).fin_tournee || {}), subject: e.target.value } })}
              data-testid="tpl-email-fin-subject"
            />
            <Label className="text-sm font-medium text-slate-700 mt-3 block">Email — Fin de tournée : Corps</Label>
            <textarea
              className="mt-1 w-full min-h-[120px] rounded-md border border-slate-200 p-2 text-sm font-mono"
              value={(params?.email_templates?.fin_tournee || {}).body || ""}
              onChange={(e) => handleChange("email_templates", { ...(params.email_templates || {}), fin_tournee: { ...((params.email_templates || {}).fin_tournee || {}), body: e.target.value } })}
              data-testid="tpl-email-fin-body"
            />
          </div>
          <p className="text-xs text-slate-400">N'oubliez pas de cliquer "Sauvegarder" en haut de page pour appliquer les changements.</p>
        </CardContent>
      </Card>

      {/* Informations Peppol / Facturation */}
      <Card data-testid="peppol-info-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-600" />
            Informations Peppol / Facturation (vendeur)
          </CardTitle>
          <CardDescription>
            Ces informations apparaîtront sur les devis, PDF et exports XML Peppol.
            Remplissez-les pour être en règle avec la facturation électronique belge.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>N° TVA (ex: BE0123.456.789)</Label>
              <Input value={params?.vendeur_tva || ""} onChange={(e) => handleChange("vendeur_tva", e.target.value)} placeholder="BE0123.456.789" data-testid="vendeur-tva" />
            </div>
            <div>
              <Label>N° BCE / entreprise</Label>
              <Input value={params?.vendeur_bce || ""} onChange={(e) => handleChange("vendeur_bce", e.target.value)} placeholder="0123.456.789" data-testid="vendeur-bce" />
            </div>
            <div>
              <Label>IBAN</Label>
              <Input value={params?.vendeur_iban || ""} onChange={(e) => handleChange("vendeur_iban", e.target.value)} placeholder="BE68 5390 0754 7034" data-testid="vendeur-iban" />
            </div>
            <div>
              <Label>BIC</Label>
              <Input value={params?.vendeur_bic || ""} onChange={(e) => handleChange("vendeur_bic", e.target.value)} placeholder="GKCCBEBB" data-testid="vendeur-bic" />
            </div>
          </div>
          <div>
            <Label>Adresse</Label>
            <Input value={params?.vendeur_adresse || ""} onChange={(e) => handleChange("vendeur_adresse", e.target.value)} placeholder="Rue de l'exemple 123" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Code postal</Label>
              <Input value={params?.vendeur_code_postal || ""} onChange={(e) => handleChange("vendeur_code_postal", e.target.value)} placeholder="1000" />
            </div>
            <div>
              <Label>Ville</Label>
              <Input value={params?.vendeur_ville || ""} onChange={(e) => handleChange("vendeur_ville", e.target.value)} placeholder="Bruxelles" />
            </div>
            <div>
              <Label>Pays (code ISO)</Label>
              <Input value={params?.vendeur_pays || "BE"} onChange={(e) => handleChange("vendeur_pays", e.target.value.toUpperCase())} maxLength={2} placeholder="BE" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Email entreprise</Label>
              <Input type="email" value={params?.vendeur_email || ""} onChange={(e) => handleChange("vendeur_email", e.target.value)} />
            </div>
            <div>
              <Label>Téléphone entreprise</Label>
              <Input value={params?.vendeur_telephone || ""} onChange={(e) => handleChange("vendeur_telephone", e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-slate-400">N'oubliez pas de cliquer "Sauvegarder" en haut de page pour appliquer les changements.</p>
        </CardContent>
      </Card>

      {/* Portail B2B — infos de contact */}
      <Card data-testid="b2b-contact-info-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Portail B2B — Infos de contact
          </CardTitle>
          <CardDescription>
            Ces coordonnées s&apos;affichent dans l&apos;onglet <b>Contact</b> de l&apos;espace B2B connecté.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Email de contact</Label>
              <Input type="email" value={params?.b2b_contact_email || ""} onChange={(e) => handleChange("b2b_contact_email", e.target.value)} placeholder="contact@topcocoon.support" data-testid="b2b-contact-email" />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={params?.b2b_contact_telephone || ""} onChange={(e) => handleChange("b2b_contact_telephone", e.target.value)} placeholder="+32 XXX XX XX XX" data-testid="b2b-contact-telephone" />
            </div>
            <div>
              <Label>Adresse / Zone</Label>
              <Input value={params?.b2b_contact_adresse || ""} onChange={(e) => handleChange("b2b_contact_adresse", e.target.value)} placeholder="Belgique" data-testid="b2b-contact-adresse" />
            </div>
          </div>
        </CardContent>
      </Card>
      <Card data-testid="facture-info-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            Société de facturation (B2B)
          </CardTitle>
          <CardDescription>
            Société sous laquelle vous émettez vos <b>factures</b>. Les <b>devis</b> resteront sous TopCocoon.
            Laissez le nom vide pour utiliser TopCocoon partout.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Raison sociale (apparaît sur les factures)</Label>
            <Input value={params?.facture_company_name || ""} onChange={(e) => handleChange("facture_company_name", e.target.value)} placeholder="Sanchelec (SPRL)" data-testid="facture-company-name" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>N° TVA</Label>
              <Input value={params?.facture_tva || ""} onChange={(e) => handleChange("facture_tva", e.target.value)} placeholder="BE0821174086" data-testid="facture-tva" />
            </div>
            <div>
              <Label>N° BCE</Label>
              <Input value={params?.facture_bce || ""} onChange={(e) => handleChange("facture_bce", e.target.value)} placeholder="0821.174.086" />
            </div>
            <div>
              <Label>IBAN</Label>
              <Input value={params?.facture_iban || ""} onChange={(e) => handleChange("facture_iban", e.target.value)} placeholder="BE32 7310 5876 5302" data-testid="facture-iban" />
            </div>
            <div>
              <Label>BIC</Label>
              <Input value={params?.facture_bic || ""} onChange={(e) => handleChange("facture_bic", e.target.value)} placeholder="CREGBEBB" />
            </div>
          </div>
          <div>
            <Label>Adresse</Label>
            <Input value={params?.facture_adresse || ""} onChange={(e) => handleChange("facture_adresse", e.target.value)} placeholder="Rue Docteur Kuborn 31/20" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Code postal</Label>
              <Input value={params?.facture_code_postal || ""} onChange={(e) => handleChange("facture_code_postal", e.target.value)} placeholder="1070" />
            </div>
            <div>
              <Label>Ville</Label>
              <Input value={params?.facture_ville || ""} onChange={(e) => handleChange("facture_ville", e.target.value)} placeholder="Anderlecht" />
            </div>
            <div>
              <Label>Pays (ISO)</Label>
              <Input value={params?.facture_pays || "BE"} onChange={(e) => handleChange("facture_pays", e.target.value.toUpperCase())} maxLength={2} placeholder="BE" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Email (optionnel)</Label>
              <Input type="email" value={params?.facture_email || ""} onChange={(e) => handleChange("facture_email", e.target.value)} />
            </div>
            <div>
              <Label>Téléphone (optionnel)</Label>
              <Input value={params?.facture_telephone || ""} onChange={(e) => handleChange("facture_telephone", e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sauvegarde Google Sheets */}
      <GoogleSheetsSync />

      {/* Équipe & comptes livreurs */}
      <TeamManager />
    </div>
  );
};

export default Parametres;
